/**
 * deriveClientRung — the CLIENT-FACING status rung for a portal row
 * (SPEC-stakeholder-portal-ux-hardening-2026-07-08, R1 AC2).
 *
 * The backend already sends `claim_ladder` = derive_claim_ladder(stage,
 * system_verification, human_resolution) — a PURE read over the three GLOBAL,
 * dev/coordinator-owned axes, SHARED by every client linked to the same work_item.
 * Those raw axes are NOT on the wire (privacy: only the derived rung + this row's
 * own `acceptance_state` are). This helper layers the row's OWN acceptance_state on
 * top of that shared rung to produce the client-facing rung:
 *
 *   • acceptance_state 'accepted'  → a "✅ You confirmed this is fixed" rung
 *   • acceptance_state 'withdrawn' → a withdrawn rung (introduced by R6; handled
 *                                    here defensively so R1 is forward-compatible)
 *   • otherwise                    → the SHARED rung (claim_ladder), unchanged
 *
 * Because it keys off THIS row's acceptance_state, a client's own accept/withdraw
 * shows on THEIR row and one client's action never rewrites the rung a co-linked
 * client sees (R1 AC2 anti-bleed). It is a PURE function (no I/O) — unit-testable
 * in isolation. It is the frontend-faithful equivalent of the AC2 "pure helper
 * derives the client-facing rung from (stage, system_verification,
 * human_resolution, this-row acceptance_state)": on the client we receive the
 * already-derived shared rung (which encodes the three axes) rather than the raw
 * axes, because the privacy read never puts the raw axes on the wire.
 *
 * The pill label is dash-free (R1 AC5 — no em/en-dash "AI tell" in the status
 * pill); the shared 'shipped_awaiting' label carries an em-dash on the wire, so we
 * clean it here for the pill.
 */
import type { FeedbackClaimLadder, FeedbackItem } from '@/lib/schemas';

export const CLIENT_CONFIRMED_RUNG: FeedbackClaimLadder = {
  key: 'you_confirmed',
  emoji: '✅',
  label: 'You confirmed this is fixed',
};

export const CLIENT_WITHDRAWN_RUNG: FeedbackClaimLadder = {
  key: 'withdrawn',
  emoji: '🚫',
  label: 'You withdrew this',
};

// v0.17.16: the operator dismissed this report from triage (acceptance_state
// 'rejected'). No email goes out; her row stops reading "Received".
export const CLIENT_CLOSED_RUNG: FeedbackClaimLadder = {
  key: 'closed',
  emoji: '📁',
  label: 'Closed',
};

// Dash-free pill-label overrides (R1 AC5). Only where the wire label carries a dash
// (or needs clearer client-facing phrasing); every other shared label passes
// through unchanged.
// v0.17.15: the reporter-facing wording for the shipped rung is "Fixed, please
// check" (pill, detail line and timeline). Internal views keep "Shipped".
const PILL_LABEL_OVERRIDE: Record<string, string> = {
  shipped_awaiting: 'Fixed, please check',
};

/** The reporter-view pill/timeline label for a rung key, or undefined to use
 *  the shared label. Reporter surfaces only (My Submissions, Your Issues). */
export function clientPillLabel(key: string): string | undefined {
  return PILL_LABEL_OVERRIDE[key];
}

type RungInput = Pick<FeedbackItem, 'claim_ladder' | 'acceptance_state'>;

export function deriveClientRung(item: RungInput): FeedbackClaimLadder {
  if (item.acceptance_state === 'accepted') return CLIENT_CONFIRMED_RUNG;
  if (item.acceptance_state === 'withdrawn') return CLIENT_WITHDRAWN_RUNG;
  if (item.acceptance_state === 'rejected') return CLIENT_CLOSED_RUNG;
  const shared = item.claim_ladder;
  const override = PILL_LABEL_OVERRIDE[shared.key];
  return override ? { ...shared, label: override } : shared;
}
